import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  CheckCircle,
  XCircle,
  Eye,
  Clock,
  MapPin,
  DollarSign,
  Calendar,
} from "lucide-react";

interface Package {
  id: string;
  title: string;
  description?: string;
  price: number;
  duration_days?: number;
  location?: string;
  status: string;
  created_at: string;
  agent: {
    id: string;
    company_name: string;
    user: {
      name: string;
      email: string;
    };
  };
}

export default function PackagesApprovalPage() {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const { data, error } = await supabase
        .from("packages")
        .select(
          `
          *,
          agent:agents(
            id,
            company_name,
            user:users(name, email)
          )
        `
        )
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPackages(data || []);
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast({
        title: "Error",
        description: "Failed to fetch packages",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (
    packageId: string,
    status: "approved" | "rejected"
  ) => {
    setActionLoading(packageId);
    try {
      const { error } = await supabase
        .from("packages")
        .update({
          status,
          ...(status === "rejected" &&
            rejectionReason && { rejection_reason: rejectionReason }),
        })
        .eq("id", packageId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Package ${status} successfully`,
      });

      fetchPackages();
      setRejectionReason("");
      setSelectedPackage(null);
    } catch (error) {
      console.error("Error updating package status:", error);
      toast({
        title: "Error",
        description: `Failed to ${status} package`,
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            Rejected
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            Pending
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const filteredPackages = (status?: string) => {
    return status ? packages.filter((pkg) => pkg.status === status) : packages;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Package Approvals</h1>
        <p className="text-gray-600 mt-1">
          Review and approve pending package submissions
        </p>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pending">
            Pending ({filteredPackages("pending").length})
          </TabsTrigger>
          <TabsTrigger value="approved">
            Approved ({filteredPackages("approved").length})
          </TabsTrigger>
          <TabsTrigger value="rejected">
            Rejected ({filteredPackages("rejected").length})
          </TabsTrigger>
          <TabsTrigger value="all">All ({packages.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          {filteredPackages("pending").map((pkg) => (
            <Card key={pkg.id} className="border-yellow-200 bg-yellow-50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-6 w-6 text-blue-600" />
                    <div>
                      <CardTitle className="text-lg">{pkg.title}</CardTitle>
                      <p className="text-sm text-gray-600">
                        by {pkg.agent?.company_name}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(pkg.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium">
                      ₹{pkg.price?.toLocaleString()}
                    </span>
                  </div>
                  {pkg.duration_days && (
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{pkg.duration_days} days</span>
                    </div>
                  )}
                  {pkg.location && (
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{pkg.location}</span>
                    </div>
                  )}
                </div>

                {pkg.description && (
                  <p className="text-sm text-gray-600">{pkg.description}</p>
                )}

                <div className="flex items-center space-x-2 pt-4 border-t">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedPackage(pkg)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Package Details</DialogTitle>
                      </DialogHeader>
                      {selectedPackage && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm font-medium">
                                Title
                              </label>
                              <p className="text-sm text-gray-600">
                                {selectedPackage.title}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium">
                                Company
                              </label>
                              <p className="text-sm text-gray-600">
                                {selectedPackage.agent?.company_name}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium">
                                Price
                              </label>
                              <p className="text-sm text-gray-600">
                                ₹{selectedPackage.price?.toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium">
                                Duration
                              </label>
                              <p className="text-sm text-gray-600">
                                {selectedPackage.duration_days ||
                                  "Not specified"}{" "}
                                days
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium">
                                Location
                              </label>
                              <p className="text-sm text-gray-600">
                                {selectedPackage.location || "Not specified"}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium">
                                Submitted Date
                              </label>
                              <p className="text-sm text-gray-600">
                                {new Date(
                                  selectedPackage.created_at
                                ).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          {selectedPackage.description && (
                            <div>
                              <label className="text-sm font-medium">
                                Description
                              </label>
                              <p className="text-sm text-gray-600">
                                {selectedPackage.description}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>

                  <Button
                    size="sm"
                    onClick={() => handleApproval(pkg.id, "approved")}
                    disabled={actionLoading === pkg.id}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={actionLoading === pkg.id}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Reject Package</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                          Please provide a reason for rejecting this package:
                        </p>
                        <Textarea
                          placeholder="Enter rejection reason..."
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          rows={3}
                        />
                        <div className="flex space-x-2">
                          <Button
                            variant="destructive"
                            onClick={() => handleApproval(pkg.id, "rejected")}
                            disabled={
                              !rejectionReason.trim() ||
                              actionLoading === pkg.id
                            }
                          >
                            Confirm Rejection
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          ))}
          {filteredPackages("pending").length === 0 && (
            <Card>
              <CardContent className="text-center py-8">
                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No pending package approvals</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="approved" className="space-y-4">
          {filteredPackages("approved").map((pkg) => (
            <Card key={pkg.id} className="border-green-200 bg-green-50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-6 w-6 text-blue-600" />
                    <div>
                      <CardTitle className="text-lg">{pkg.title}</CardTitle>
                      <p className="text-sm text-gray-600">
                        by {pkg.agent?.company_name}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(pkg.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium">
                      ₹{pkg.price?.toLocaleString()}
                    </span>
                  </div>
                  {pkg.duration_days && (
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{pkg.duration_days} days</span>
                    </div>
                  )}
                  {pkg.location && (
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{pkg.location}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {filteredPackages("approved").length === 0 && (
            <Card>
              <CardContent className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No approved packages</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="rejected" className="space-y-4">
          {filteredPackages("rejected").map((pkg) => (
            <Card key={pkg.id} className="border-red-200 bg-red-50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-6 w-6 text-blue-600" />
                    <div>
                      <CardTitle className="text-lg">{pkg.title}</CardTitle>
                      <p className="text-sm text-gray-600">
                        by {pkg.agent?.company_name}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(pkg.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium">
                      ₹{pkg.price?.toLocaleString()}
                    </span>
                  </div>
                  {pkg.duration_days && (
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{pkg.duration_days} days</span>
                    </div>
                  )}
                  {pkg.location && (
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{pkg.location}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {filteredPackages("rejected").length === 0 && (
            <Card>
              <CardContent className="text-center py-8">
                <XCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No rejected packages</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="all" className="space-y-4">
          {packages.map((pkg) => (
            <Card key={pkg.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-6 w-6 text-blue-600" />
                    <div>
                      <CardTitle className="text-lg">{pkg.title}</CardTitle>
                      <p className="text-sm text-gray-600">
                        by {pkg.agent?.company_name}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(pkg.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium">
                      ₹{pkg.price?.toLocaleString()}
                    </span>
                  </div>
                  {pkg.duration_days && (
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{pkg.duration_days} days</span>
                    </div>
                  )}
                  {pkg.location && (
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{pkg.location}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
